import { CriteoVO } from "@/vo/criteo.vo";
import { isMobile } from "mobile-device-detect";
import Vue from 'vue';
import CommonUtils from "./common-utils";

export default class Criteo extends Vue{
    /* 
        크리테오 OneTag 모드 구성

        홈페이지 태그                    : HOMEPAGE_TAG [1]
        카테고리/검색/리스팅 태그         : CATEGORY_SEARCH_LISTING_TAG [2]
        상품 태그                        : PRODUCT_TAG [3]
        장바구니에 추가 태그              : CART_ADD_TAG [4]
        장바구니 태그                    : CART_TAG [5]
        세일즈 태그                      : SALES_TAG [6]
        세일즈 태그 (네이버페이 주문형)   : SALES_NAVER_TAG [7]

        [router.afterEach 를 통해서 호출]
         HOMEPAGE_TAG

        [각 프로그램 내 직접 호출 (구조상 어쩔 수 없음)]
         PRODUCT_TAG, CATEGORY_SEARCH_LISTING_TAG, CART_ADD_TAG, CART_TAG, SALES_TAG

        [미사용]
         SALES_NAVER_TAG

        크리테오 OneTag 가이드
         https://guides.criteotilt.com/onetag/onetag_ko/?accountid=97498


        크리티오 request 사용 예
        Criteo.putQuery({
            path : to.name,
            oneTag : Criteo.criteoTags.HOMEPAGE_TAG
        } as CriteoVO);

    */
    static criteoTags = {
        NONE_TAG                        :0,
        HOMEPAGE_TAG                    :1,
        CATEGORY_SEARCH_LISTING_TAG     :2,
        PRODUCT_TAG                     :3,
        CART_ADD_TAG                    :4,
        CART_TAG                        :5,
        SALES_TAG                       :6,
        SALES_NAVER_TAG                 :7
    };

    // 호출 경로에 따른 태그 설정
    static getOneTag(criteoObj: CriteoVO): CriteoVO{
        // 세션에 멤버정보 존재 시 이메일 주소 가져옴 (없을 시 NONE 태그 설정)
        if(Vue.prototype.$session.has("memberInfo")){
            // 이메일
            criteoObj.userEmail = Vue.prototype.$session.get("memberInfo").memEmail;            

            // 모바일? 데스크탑?
            criteoObj.deviceType = isMobile.valueOf() ? 'm' : 'd';
        }
        else{
            criteoObj.oneTag = this.criteoTags.NONE_TAG;    
        }

        // 메인페이지에서만 호출될 수 있도록 태그를 조정
        if((criteoObj.path == "Main") && (criteoObj.oneTag == this.criteoTags.HOMEPAGE_TAG)){
            criteoObj.oneTag = this.criteoTags.HOMEPAGE_TAG;
        }
        else if((criteoObj.path != "Main") && (criteoObj.oneTag == this.criteoTags.HOMEPAGE_TAG)){
            criteoObj.oneTag = this.criteoTags.NONE_TAG;
        }

        // data가 전달되지 않은 호출일 때 빈 배열을 등록해줌
        if(criteoObj.data == undefined){
            criteoObj.data = [];
        }

        return criteoObj;
    }

    // 크리테오 호출을 위한 오브젝트 설정
    static setCriteoDataSet(criteoObj: CriteoVO): Map<string, any>[]{
        const items = [] as any;

        // 상품코드, 상품가격 배열 초기화 (미초기화 시 에러 발생)
        criteoObj.productId = [];
        criteoObj.unitPrice = [];

        // 상품ID, 판매가격에 대한 데이터를 각 태그에 맞게 가져옴
        for(let i=0;i<3;i++){
            if(!criteoObj.data[i]) break;

            const data: any = criteoObj.data[i];

            switch(criteoObj.oneTag){
                case this.criteoTags.CATEGORY_SEARCH_LISTING_TAG:
                case this.criteoTags.PRODUCT_TAG:
                case this.criteoTags.CART_TAG:
                case this.criteoTags.CART_ADD_TAG:
                    criteoObj.productId.push(data.productCd);
                    criteoObj.unitPrice.push(data.salePrice);
                break;

                case this.criteoTags.SALES_TAG:
                    criteoObj.productId.push(data.item_id);
                    criteoObj.unitPrice.push(data.price);
                break;

                default:
                break;
            }
        }

        // 기본정보 설정 (크리테오 고객번호, 이메일, 우편번호)
        const criteoDataSet: any = [
            {event:"setAccount", account:97498},
            {event:"setEmail", email: criteoObj.userEmail, "hash_method":"sha256"},
            {event:"setZipcode", zipcode:""},
            {event:"setSiteType", type:criteoObj.deviceType}
        ];

        // 크리테오에 최종적으로 전송할 데이터를 가공함
        switch(criteoObj.oneTag){
            case this.criteoTags.HOMEPAGE_TAG:
                criteoDataSet.push(
                    {
                        event:"viewHome"
                    }
                );
            break;
            
            case this.criteoTags.CATEGORY_SEARCH_LISTING_TAG: 
                criteoDataSet.push(
                    {
                        event:"viewList",
                        item:criteoObj.productId,
                        category:criteoObj.categoryId,
                        keyword:criteoObj.searchKeyword
                    }
                );
            break;
            
            case this.criteoTags.PRODUCT_TAG: 
                criteoDataSet.push(
                    {
                        event:"viewItem",
                        item:criteoObj.productId[0],
                    }
                );
            break;
            
            case this.criteoTags.CART_ADD_TAG: 
                criteoDataSet.push(
                    {
                        event:"addToCart",
                        item:
                        [
                            {
                                id:criteoObj.productId[0],
                                price:criteoObj.unitPrice[0],
                                quantity:1
                            }
                        ],
                    }
                );
            break;
            
            case this.criteoTags.CART_TAG: 
                for(const idx in criteoObj.productId){
                    items.push(
                        {
                            id:criteoObj.productId[idx],
                            price:criteoObj.unitPrice[idx],
                            quantity:1
                        }
                    );
                }

                criteoDataSet.push(
                    {
                        event:"viewBasket",
                        item:items
                    }
                );
            break;
            
            case this.criteoTags.SALES_TAG: 
                for(const idx in criteoObj.productId){
                    items.push(
                        {
                            id:criteoObj.productId[idx],
                            price:criteoObj.unitPrice[idx],
                            quantity:1
                        }
                    );
                }

                criteoDataSet.push(
                    {
                        event:"trackTransaction",
                        id:criteoObj.transactionId,
                        item:items
                    }
                );
            break;
            
            case this.criteoTags.SALES_NAVER_TAG: 
            break;
        }

        return criteoDataSet;
    }

    // criteo 사이트에 정보 전달
    static putQuery(criteoObj: CriteoVO){
        if (process.env.NODE_ENV != 'production' || CommonUtils.isSystemLocale('cn') == true || CommonUtils.isSystemLocale('global') == true) {
            return;
        }

        // 홈페이지 태그는 메인페이지에서만 실행될 수 있도록 태그를 수정
        criteoObj = this.getOneTag(criteoObj);

        // 수정된 태그가 NONE일 경우 호출하지 않고 종료
        if(criteoObj.oneTag == this.criteoTags.NONE_TAG) return;

        const _window = (window as any);
        const query = _window.criteo_q.push;
        const dataset = this.setCriteoDataSet(criteoObj);
        
        // 호출할 경우 데이터셋 전달
        query(dataset);
    }
}